import { Component, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { BaseContainer, ExportTagType, RequestPaginationInfo, SortDirection, SortInfo } from '@stylehaven/components';
import { PaginatedResults } from '../../../../entities';
import { ExportEntity } from '../../../entities/downloads/export-entity.entity';
import { DEFAULT_PAGE_SIZE } from '../../../helpers/constants';
import { HIERARCHY_TABLE_SCROLL_REF } from '../../../record/components/hierarchical-list/hierarchical-list.component';
import { Column } from '../../../record/entities/column.entity';
import {
    HierarchyRow,
    toChildRow,
    toMessageRow,
    toRecordRow,
    toSubheaderRow
} from '../../../record/entities/hierarchy-row.entity';
import { PaginationInfo } from '../../../record/entities/pagination/pagination-info.entity';
import {
    DownloadGroupRow,
    DownloadJobRow,
    DownloadRow,
    PendingPartRow,
    pendingPartKey
} from '../../entities/download-row.entity';
import { toDownloadColumns } from '../../helpers/download-columns.helper';
import { DownloadGrouping, groupDownloads, sortDownloadGroupings } from '../../helpers/download-grouping.helper';
import { DownloadColumn } from '../../services/downloads-column-handler.service';
import { DownloadsSandboxService } from '../../sandbox/downloads-sandbox.service';

@Component({
    standalone: false,
    selector: 'edp-downloads-overview-container',
    templateUrl: './downloads-overview-container.component.html',
    styleUrls: ['./downloads-overview-container.component.scss'],
    host: { class: 'dgh-overview-page' }
})
export class DownloadsOverviewContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly POLLING_INTERVAL: number = 5000;

    readonly paginationScrollRef: string = HIERARCHY_TABLE_SCROLL_REF;

    private readonly fetchSize$: BehaviorSubject<number> = new BehaviorSubject<number>(DEFAULT_PAGE_SIZE);

    readonly currentSort$: BehaviorSubject<SortInfo> = new BehaviorSubject<SortInfo>({
        field: DownloadColumn.Downloaddatum,
        direction: SortDirection.DESC
    });

    readonly page$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    readonly pageSize$: BehaviorSubject<number> = new BehaviorSubject<number>(DEFAULT_PAGE_SIZE);

    readonly expandedKeys$: BehaviorSubject<ReadonlySet<string>> = new BehaviorSubject<ReadonlySet<string>>(
        new Set()
    );

    pagedResult$!: Observable<PaginatedResults<ExportEntity>>;

    downloads$!: Observable<ExportEntity[]>;
    topRows$!: Observable<DownloadRow[]>;
    rows$!: Observable<HierarchyRow<DownloadRow>[]>;
    paginationInfo$!: Observable<PaginationInfo>;
    columns$!: Observable<Column<DownloadRow>[]>;

    tableHeaderWithCount$!: Observable<string>;

    private readonly rowsByKey: Map<string, DownloadJobRow> = new Map();
    private readonly groupsByKey: Map<string, DownloadGroupRow> = new Map();
    private readonly pendingByKey: Map<string, PendingPartRow> = new Map();

    constructor(private readonly sandbox: DownloadsSandboxService) {
        super();
    }

    ngOnInit(): void {
        this.initSourceStreams();

        this.initTableHeaderStream();
        this.initColumns();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.fetchSize$.complete();
        this.currentSort$.complete();
        this.page$.complete();
        this.pageSize$.complete();
        this.expandedKeys$.complete();
        this.rowsByKey.clear();
        this.groupsByKey.clear();
        this.pendingByKey.clear();
    }

    paginationInfoChanged(paginationInfo: RequestPaginationInfo) {
        if (paginationInfo.resultsPerPage !== undefined && paginationInfo.resultsPerPage !== this.pageSize$.value) {
            this.pageSize$.next(paginationInfo.resultsPerPage);
        }
        if (paginationInfo.page !== undefined) {
            this.page$.next(paginationInfo.page);
        }
    }

    private initColumns(): void {
        this.columns$ = of(toDownloadColumns(this.translateService));
    }

    private initTableHeaderStream() {
        this.tableHeaderWithCount$ = this.downloads$.pipe(
            map(jobs => this.translateService.instant('downloads.results', { items: jobs.length }))
        );
    }

    private initSourceStreams(): void {
        this.pagedResult$ = this.fetchSize$.pipe(
            distinctUntilChanged(),
            tap(() => this.startLoading()),
            switchMap(size => this.getDownloadsWithPolling(size)),
            tap(result => this.syncFetchSize(result.paginationInfo.totalNumberOfResults)),
            tap(() => this.stopLoading()),
            takeUntil(this.destroy$),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.downloads$ = this.pagedResult$.pipe(
            map(pagedResult => pagedResult.results ?? []),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        // Off `pagedResult$` rather than `downloads$`: the rows need the jobs and how complete they are, and
        // combining the two as separate streams would render one poll's jobs against the previous one's total.
        this.topRows$ = combineLatest([this.pagedResult$, this.currentSort$]).pipe(
            map(([result, sort]) => this.toTopRows(result.results ?? [], this.isComplete(result), sort)),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.rows$ = combineLatest([this.topRows$, this.page$, this.pageSize$, this.expandedKeys$]).pipe(
            map(([rows, page, size, expanded]) => this.flatten(rows.slice(page * size, page * size + size), expanded)),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.paginationInfo$ = combineLatest([this.topRows$, this.page$, this.pageSize$]).pipe(
            map(([rows, page, resultsPerPage]) => ({
                page,
                resultsPerPage,
                totalNumberOfResults: rows.length
            })),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private syncFetchSize(total: number): void {
        if (total > 0 && total !== this.fetchSize$.value) {
            this.fetchSize$.next(total);
        }
    }

    onExpandToggled(row: HierarchyRow<DownloadRow>): void {
        const next: Set<string> = new Set(this.expandedKeys$.value);

        if (next.has(row.key)) {
            next.delete(row.key);
        } else {
            next.add(row.key);
        }

        this.expandedKeys$.next(next);
    }

    onCollapseAll(): void {
        this.expandedKeys$.next(new Set());
    }

    onSortInfoChange(sort: SortInfo): void {
        this.currentSort$.next(sort);
        this.page$.next(0);
    }

    /**
     * Whether the page is looking at the whole list. The first poll asks for a default page and only learns the
     * real total from its answer, so until the next one lands a group can be short of delen that do exist.
     */
    private isComplete(result: PaginatedResults<ExportEntity>): boolean {
        const total: number = result.paginationInfo.totalNumberOfResults ?? 0;

        return (result.results ?? []).length >= total;
    }

    private toTopRows(jobs: ExportEntity[], complete: boolean, sort: SortInfo): DownloadRow[] {
        const seenJobs: Set<string> = new Set();
        const seenGroups: Set<string> = new Set();

        const ordered: DownloadGrouping[] = sortDownloadGroupings(groupDownloads(jobs ?? []), sort.direction);

        const rows: DownloadRow[] = ordered.map(grouping => {
            if (grouping.kind === 'flat') {
                seenJobs.add(grouping.job.ExportJobId);
                return this.jobWrapperFor(grouping.job);
            }

            seenGroups.add(grouping.key);
            grouping.parts.forEach(part => seenJobs.add(part.job.ExportJobId));

            return this.groupWrapperFor(grouping, complete);
        });

        this.dropUnseen(this.rowsByKey, seenJobs);
        this.dropUnseen(this.groupsByKey, seenGroups);
        this.dropArrivedPending();

        return rows;
    }

    /** A deel that has arrived has a row of its own; its stand-in has to go, or both would claim the place. */
    private dropArrivedPending(): void {
        const awaited: Set<string> = new Set(
            [...this.groupsByKey.values()].flatMap(group =>
                group.missingParts.map(part => pendingPartKey(group.key, part))
            )
        );

        this.dropUnseen(this.pendingByKey, awaited);
    }

    private flatten(rows: DownloadRow[], expanded: ReadonlySet<string>): HierarchyRow<DownloadRow>[] {
        return rows.flatMap(row => {
            if (!(row instanceof DownloadGroupRow)) {
                return [toRecordRow<DownloadRow>(row.key, of(row), false)];
            }

            const isExpanded: boolean = expanded.has(row.key);
            const parent: HierarchyRow<DownloadRow> = toRecordRow<DownloadRow>(row.key, of(row), false, {
                expandable: true,
                expanded: isExpanded
            });

            if (!isExpanded) {
                return [parent];
            }

            return [parent, toSubheaderRow<DownloadRow>(row.key), ...this.toDelenRows(row)];
        });
    }

    private toDelenRows(group: DownloadGroupRow): HierarchyRow<DownloadRow>[] {
        const byPart: Map<number, DownloadJobRow> = new Map(group.parts.map(part => [part.partNumber, part]));

        return Array.from({ length: group.total }, (_, index) => index + 1).map(part => {
            const present: DownloadJobRow | undefined = byPart.get(part);

            if (present) {
                return toChildRow<DownloadRow>(present.key, group.key, of(present));
            }

            // Still being made: a row of its own, carrying the name it will have and the wait it is in. An
            // inline message could carry neither — it spans the columns instead of filling them.
            if (group.gapKind === 'pending') {
                const awaited: PendingPartRow = this.pendingRowFor(group, part);

                return toChildRow<DownloadRow>(awaited.key, group.key, of(awaited));
            }

            return toMessageRow<DownloadRow>(group.key, 'downloads.messages.deel-unavailable', false, {
                keySuffix: `${part}`,
                messageParams: { n: part }
            });
        });
    }

    private pendingRowFor(group: DownloadGroupRow, part: number): PendingPartRow {
        const key: string = pendingPartKey(group.key, part);
        const existing: PendingPartRow | undefined = this.pendingByKey.get(key);

        if (existing) {
            return existing;
        }

        const created: PendingPartRow = new PendingPartRow(group.key, part, group.total);
        this.pendingByKey.set(key, created);

        return created;
    }

    private jobWrapperFor(job: ExportEntity): DownloadJobRow {
        const existing: DownloadJobRow | undefined = this.rowsByKey.get(job.ExportJobId);

        if (existing) {
            existing.update(job);
            return existing;
        }

        const created: DownloadJobRow = new DownloadJobRow(job);
        this.rowsByKey.set(job.ExportJobId, created);

        return created;
    }

    private groupWrapperFor(grouping: DownloadGrouping & { kind: 'group' }, complete: boolean): DownloadGroupRow {
        const parts: DownloadJobRow[] = grouping.parts.map(part => this.jobWrapperFor(part.job));
        const existing: DownloadGroupRow | undefined = this.groupsByKey.get(grouping.key);

        if (existing) {
            existing.update(grouping.total, parts, complete);
            return existing;
        }

        const created: DownloadGroupRow = new DownloadGroupRow(grouping.key, grouping.total, parts, complete);
        this.groupsByKey.set(grouping.key, created);

        return created;
    }

    private dropUnseen(cache: Map<string, unknown>, seen: ReadonlySet<string>): void {
        [...cache.keys()].filter(key => !seen.has(key)).forEach(key => cache.delete(key));
    }

    private getDownloadsWithPolling(size: number): Observable<PaginatedResults<ExportEntity>> {
        return timer(0, this.POLLING_INTERVAL).pipe(
            concatMap(() => this.getDownloads(size)),
            catchError(() => this.onGetDownloadsError())
        );
    }

    private getDownloads(size: number): Observable<PaginatedResults<ExportEntity>> {
        return this.sandbox.getDownloads({
            startIndex: 0,
            nrOfResults: size,
            tag: ExportTagType.Download
        });
    }

    private startLoading(): void {
        this.setError(false);
        this.setLoading(true);
    }

    private stopLoading(): void {
        this.setError(false);
        this.setLoading(false);
    }

    private onGetDownloadsError(): Observable<any> {
        this.setLoading(false);
        this.setError(true);

        return EMPTY;
    }
}
